/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { jwtDecode } from 'jwt-decode';
import { premiumPluginIdentifiers } from '../../constants/premiumPluginIdentifiers';
import { getLicenseKeyFromUserData } from '../getLicenseKeyFromUserData/getLicenseKeyFromUserData';
import type { FeatureName } from '../../constants/capabilities';
import type { CustomerPortalIntegrationType } from '../../contexts/CustomerPortalContext';

type CKEditor5LicenseKeyData = {
	features?: Array<keyof typeof premiumPluginIdentifiers>;
};

export function getFeaturesFromToken( userData: CustomerPortalIntegrationType[ 'customerPortalAccount' ] ): Array<FeatureName> {
	if ( !userData ) {
		return [];
	}

	const availableFeatures = getFeatureCodesFromLicense( userData );

	const allFeatures = availableFeatures.flatMap( featureCode => {
		if ( featureCode === '*' ) {
			return Object.values( premiumPluginIdentifiers ).flat();
		}

		return premiumPluginIdentifiers[ featureCode ];
	} );

	return [ ...new Set( allFeatures ) ];
}

function getFeatureCodesFromLicense(
	userData: CustomerPortalIntegrationType[ 'customerPortalAccount' ]
): Array<keyof typeof premiumPluginIdentifiers> {
	const licenseKey = getLicenseKeyFromUserData( userData );

	if ( !licenseKey ) {
		return [];
	}

	const decodedJwtKey: CKEditor5LicenseKeyData = jwtDecode( licenseKey );

	return decodedJwtKey.features || [];
}
